import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import { 
  ArrowLeft, Check, X, Clock, User, Phone, Mail, MapPin,
  Star, Shield, Building, Search, Filter, Plus, Trash2,
  Eye, CheckCircle, XCircle, AlertCircle
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const statusConfig = {
  pending: { label: "In Attesa", color: "bg-amber-100 text-amber-700", icon: Clock },
  approved: { label: "Approvato", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  rejected: { label: "Rifiutato", color: "bg-red-100 text-red-700", icon: XCircle }
};

const professionLabels = {
  dog_sitter: "Dog Sitter",
  cinofilo: "Educatore Cinofilo",
  pensione: "Pensione/Asilo",
  toelettatura: "Toelettatura",
  veterinario: "Veterinario",
  assicurazione: "Assicurazioni",
  negozio: "Negozio Animali"
};

export default function AdminProfessionals() {
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, approved, rejected
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPro, setSelectedPro] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    fetchProfessionals();
  }, [filter]);

  const fetchProfessionals = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/professionals`;
      if (filter !== "all") url += `?status=${filter}`;
      
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setProfessionals(data);
        
        // Count pending
        const pending = data.filter(p => p.status === "pending").length;
        setPendingCount(pending);
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (professionalId, verify = false) => {
    try {
      const response = await authFetch(`${API}/admin/professionals/${professionalId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          status: "approved", 
          verified: verify,
          active: true 
        })
      });
      
      if (response.ok) {
        toast.success(verify ? "Approvato e Certificato!" : "Approvato!");
        fetchProfessionals();
        setSelectedPro(null);
      }
    } catch (error) {
      toast.error("Errore nell'approvazione");
    }
  };

  const handleReject = async (professionalId) => {
    try {
      const response = await authFetch(`${API}/admin/professionals/${professionalId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "rejected", active: false })
      });
      
      if (response.ok) {
        toast.success("Richiesta rifiutata");
        fetchProfessionals();
        setSelectedPro(null);
      }
    } catch (error) {
      toast.error("Errore nel rifiuto");
    }
  };

  const handleToggleVerified = async (professionalId, currentVerified) => {
    try {
      const response = await authFetch(`${API}/admin/professionals/${professionalId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verified: !currentVerified })
      });
      
      if (response.ok) {
        toast.success(!currentVerified ? "Certificato Zampa assegnato!" : "Certificazione rimossa");
        fetchProfessionals();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async (professionalId) => {
    if (!confirm("Sei sicuro di voler eliminare questo professionista?")) return;
    
    try {
      const response = await authFetch(`${API}/admin/professionals/${professionalId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success("Professionista eliminato");
        fetchProfessionals();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const filteredPros = professionals.filter(p => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return p.name?.toLowerCase().includes(query) || 
             p.city?.toLowerCase().includes(query) ||
             p.email?.toLowerCase().includes(query);
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#1D1D1F]">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 dark:bg-[#1D1D1F]/90 backdrop-blur-xl border-b border-black/5 dark:border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center">
                <ArrowLeft size={20} className="dark:text-white" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#1D1D1F] dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Gestione Professionisti
                </h1>
                <p className="text-sm text-[#86868B]">Admin Panel</p>
              </div>
            </div>
            
            {pendingCount > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-700">
                <Clock size={16} />
                <span className="font-medium">{pendingCount} in attesa</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Filters & Search */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex gap-2">
            {["all", "pending", "approved", "rejected"].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                  filter === f
                    ? 'bg-[#1D1D1F] text-white dark:bg-white dark:text-[#1D1D1F]'
                    : 'bg-white dark:bg-white/10 text-[#86868B] hover:bg-gray-100 dark:hover:bg-white/20'
                }`}
              >
                {f === "all" ? "Tutti" : statusConfig[f]?.label || f}
              </button>
            ))}
          </div>
          
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca per nome, città, email..."
                className="pl-10 h-10 rounded-xl bg-white dark:bg-white/10"
              />
            </div>
          </div>

          <Button onClick={() => navigate('/professional/register')} className="bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl">
            <Plus size={18} className="mr-2" />
            Aggiungi Manualmente
          </Button>
        </div>

        {/* Professionals List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-[#FF6B4A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPros.length === 0 ? (
          <div className="bg-white dark:bg-white/5 rounded-2xl p-12 text-center">
            <User className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-[#86868B]">Nessun professionista trovato</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPros.map(pro => {
              const status = statusConfig[pro.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              
              return (
                <div 
                  key={pro.professional_id}
                  className="bg-white dark:bg-white/5 rounded-2xl p-4 border border-black/5 dark:border-white/10"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center flex-shrink-0">
                        <User className="text-white" size={24} />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-[#1D1D1F] dark:text-white">{pro.name}</h3>
                          {pro.verified && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                              <CheckCircle size={12} /> Certificato
                            </span>
                          )}
                          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                            <StatusIcon size={12} /> {status.label}
                          </span>
                        </div>
                        
                        <p className="text-sm text-[#86868B] dark:text-gray-400">
                          {professionLabels[pro.profession_type] || pro.profession_type} • {pro.city}
                          {pro.zone && ` • ${pro.zone}`}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm text-[#86868B]">
                          {pro.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} /> {pro.phone}
                            </span>
                          )}
                          {pro.email && (
                            <span className="flex items-center gap-1">
                              <Mail size={14} /> {pro.email}
                            </span>
                          )}
                          {pro.price_from && (
                            <span className="font-medium text-emerald-600">Da €{pro.price_from}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedPro(pro)}
                        className="rounded-xl"
                      >
                        <Eye size={16} />
                      </Button>
                      
                      {pro.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(pro.professional_id, true)}
                            className="rounded-xl bg-emerald-500 hover:bg-emerald-600"
                          >
                            <Check size={16} className="mr-1" /> Approva
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(pro.professional_id)}
                            className="rounded-xl text-red-500 border-red-200 hover:bg-red-50"
                          >
                            <X size={16} />
                          </Button>
                        </>
                      )}
                      
                      {pro.status === "approved" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleVerified(pro.professional_id, pro.verified)}
                          className={`rounded-xl ${pro.verified ? 'text-blue-600 border-blue-200' : ''}`}
                        >
                          <Shield size={16} className="mr-1" />
                          {pro.verified ? 'Rimuovi ✓' : 'Certifica'}
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(pro.professional_id)}
                        className="rounded-xl text-red-500 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedPro} onOpenChange={() => setSelectedPro(null)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedPro?.name}
              {selectedPro?.verified && <CheckCircle className="text-blue-500" size={18} />}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPro && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#86868B]">Tipo</p>
                  <p className="font-medium">{professionLabels[selectedPro.profession_type]}</p>
                </div>
                <div>
                  <p className="text-[#86868B]">Stato</p>
                  <p className={`font-medium ${statusConfig[selectedPro.status]?.color.replace('bg-', 'text-').replace('-100', '-700')}`}>
                    {statusConfig[selectedPro.status]?.label}
                  </p>
                </div>
                <div>
                  <p className="text-[#86868B]">Città</p>
                  <p className="font-medium">{selectedPro.city}</p>
                </div>
                <div>
                  <p className="text-[#86868B]">Zona</p>
                  <p className="font-medium">{selectedPro.zone || "-"}</p>
                </div>
                <div>
                  <p className="text-[#86868B]">Telefono</p>
                  <p className="font-medium">{selectedPro.phone}</p>
                </div>
                <div>
                  <p className="text-[#86868B]">Email</p>
                  <p className="font-medium">{selectedPro.email}</p>
                </div>
                <div>
                  <p className="text-[#86868B]">Prezzo da</p>
                  <p className="font-medium">{selectedPro.price_from ? `€${selectedPro.price_from}` : "-"}</p>
                </div>
                <div>
                  <p className="text-[#86868B]">Esperienza</p>
                  <p className="font-medium">{selectedPro.experience_years ? `${selectedPro.experience_years} anni` : "-"}</p>
                </div>
              </div>
              
              <div>
                <p className="text-[#86868B] text-sm mb-1">Descrizione</p>
                <p className="text-sm">{selectedPro.description}</p>
              </div>
              
              {selectedPro.services?.length > 0 && (
                <div>
                  <p className="text-[#86868B] text-sm mb-2">Servizi</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPro.services.map((s, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedPro.certifications?.length > 0 && (
                <div>
                  <p className="text-[#86868B] text-sm mb-2">Certificazioni</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPro.certifications.map((c, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center gap-1">
                        <Shield size={12} /> {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedPro.status === "pending" && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => handleApprove(selectedPro.professional_id, true)}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Approva e Certifica
                  </Button>
                  <Button
                    onClick={() => handleApprove(selectedPro.professional_id, false)}
                    variant="outline"
                    className="flex-1 rounded-xl"
                  >
                    <Check size={18} className="mr-2" />
                    Solo Approva
                  </Button>
                  <Button
                    onClick={() => handleReject(selectedPro.professional_id)}
                    variant="outline"
                    className="rounded-xl text-red-500 border-red-200 hover:bg-red-50"
                  >
                    <X size={18} />
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
